// Per-user Google Workspace access via Google Identity Services (GIS).
// READ-ONLY by design: gmail.readonly + drive.readonly + calendar.readonly,
// requested incrementally on top of the existing Firebase sign-in. Everything
// runs as the signed-in user in their own browser — no server-side delegation,
// no track@ involvement, and existing permissions apply unchanged. Nothing is
// ever written to Gmail, Drive or Calendar from here.
import { GOOGLE_OAUTH_CLIENT_ID } from './config.js';
import { resolveShortcut } from './drive-folders.js';
import { pacedFetch } from './gmail-pace.js';
import { mapStream } from './stream-pool.js';

export const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/calendar.readonly',
].join(' ');

// Access token is CACHED (Karan 13/07 — "do I have to connect every time?").
// Persisted in localStorage with its expiry so reopening/reloading Track within
// the token's ~1-hour life doesn't re-trigger Google's connect flow. Scopes are
// READ-ONLY (gmail/drive/calendar.readonly) and the token is short-lived and
// cleared on sign-out, so the exposure is small; the UX win is large. When it
// does expire, GIS refreshes it SILENTLY (prompt:'') for a user who's already
// granted — no click.
const TOKEN_KEY = 'track-gtoken';
// Whether this person has EVER granted access on this device. A silent renewal
// is only fair to attempt for somebody who has already said yes once; a
// first-time user must be asked, from a click.
const GRANTED_KEY = 'track-gtoken-granted';
let token = null;      // { value, expiresAt }
let tokenClient = null;
let userHint = '';

function loadStoredToken() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    return (t && t.value && t.expiresAt > Date.now()) ? t : null;
  } catch (e) { return null; }
}
function storeToken(t) {
  try { t ? localStorage.setItem(TOKEN_KEY, JSON.stringify(t)) : localStorage.removeItem(TOKEN_KEY); }
  catch (e) { /* storage may be unavailable — memory token still works */ }
  if (!t) clearScoped();   // sign-out / disconnect / 401 forget the write tokens too
}
token = loadStoredToken(); // rehydrate on load

// ── Server-held grant (Karan 06/10/2026) ─────────────────────────────────────
// The token model above lasts an hour and renews only through a popup, which a
// browser opens only from a click — so after the hour every reload showed
// Connect again. When the app wires a server caller in (setGoogleServer, from
// app.js), Connect runs the CODE flow once, functions/google-grant.js keeps the
// refresh token, and every renewal is a plain call to googleToken: no popup.
let server = null;          // (name, data) => Promise<data> — a Firebase callable
export function setGoogleServer(fn) { server = typeof fn === 'function' ? fn : null; }
export function hasGoogleServer() { return Boolean(server); }

const asToken = (r) => ({ value: r.access_token, expiresAt: Date.now() + (Number(r.expires_in || 3600) - 60) * 1000 });

// WHICH SCOPES THE SERVER GRANT HOLDS, remembered on this device (07/10/2026).
// A write scope used to be asked of the server FIRST and only then, if missing,
// of Google — but the server call (often a 3-4s cold start) spends the click,
// and Safari blocks a popup that is not opened in the click itself. Every
// receipt upload by someone without the Drive scope failed at Save. Knowing the
// answer locally lets a missing scope open Google's consent at once.
const GRANT_SCOPES_KEY = 'track-grant-scopes';
function grantScopes() { try { return new Set(String(localStorage.getItem(GRANT_SCOPES_KEY) || '').split(/\s+/).filter(Boolean)); } catch (e) { return new Set(); } }
function rememberScopes(scope) {
  if (!scope) return;
  try {
    const all = grantScopes();
    String(scope).split(/\s+/).filter(Boolean).forEach((x) => all.add(x));
    localStorage.setItem(GRANT_SCOPES_KEY, [...all].join(' '));
  } catch (e) { /* storage blocked: we simply ask the server first */ }
}
function forgetScopes() { try { localStorage.removeItem(GRANT_SCOPES_KEY); } catch (e) { /* ignore */ } }

/** A fresh token from the server's grant, or null (no grant / not granted that scope / offline). */
async function serverToken(scope) {
  if (!server) return null;
  try {
    const r = await server('googleToken', scope ? { scope } : {});
    if (r && r.needsConnect) forgetScopes();
    if (r && r.access_token && !scope) rememberScopes(r.scope);   // the full grant, as Google reports it
    return r && r.access_token ? asToken(r) : null;
  } catch (e) { return null; }
}

/** Code flow from a click: Google consent → googleConnect stores the grant. */
function connectViaServer(scope) {
  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope,
      ux_mode: 'popup',
      login_hint: userHint,
      hint: userHint,
      include_granted_scopes: true,
      // A refresh token is only issued from a consent screen — without this a
      // returning person gets a code that renews nothing.
      prompt: 'consent',
      callback: async (res) => {
        if (!res || !res.code) { reject(new Error((res && res.error) || 'Google connection cancelled')); return; }
        try {
          const r = await server('googleConnect', { code: res.code });
          rememberScopes(r && r.scope);
          try { localStorage.setItem(GRANTED_KEY, '1'); } catch (e) { /* private mode */ }
          resolve(asToken(r));
        } catch (e) { reject(new Error((e && e.message) || 'Google connection failed')); }
      },
      error_callback: (err) => reject(new Error((err && (err.message || err.type)) || 'Google connection cancelled')),
    });
    client.requestCode();
  });
}

export function initGoogle(email) { userHint = email || ''; }

// Sending for the company (go-live A7, 26/10/2026): the Owner adds gmail.send to
// their grant from a click; setCompanySendFrom then makes their address the
// company's send-from. include_granted_scopes keeps what was already allowed.
export const GMAIL_SEND_SCOPE = 'https://www.googleapis.com/auth/gmail.send';
export function connectForSending() {
  if (!isConfigured()) return Promise.reject(new Error('Google connection not configured'));
  if (!server) return Promise.reject(new Error('Connecting needs the server: sign in again.'));
  return connectViaServer(`${GOOGLE_SCOPES} ${GMAIL_SEND_SCOPE}`).then((t) => { token = t; storeToken(t); return t.value; });
}

export function isConfigured() {
  return Boolean(GOOGLE_OAUTH_CLIENT_ID) &&
    typeof google !== 'undefined' && google.accounts && google.accounts.oauth2;
}

export function hasToken() {
  return Boolean(token && token.expiresAt > Date.now());
}

/**
 * Ensure we hold a live access token. Must be called from a user gesture the
 * first time (GIS opens a consent/account popup; it auto-closes on re-grants).
 * `prompt` is '' from a click; resumeToken passes 'none' so a renewal that
 * needs the person fails straight away instead of leaving a chooser open.
 */
export function ensureToken(prompt = '') {
  if (hasToken()) return Promise.resolve(token.value);
  if (!isConfigured()) return Promise.reject(new Error('Google connection not configured'));
  // From a click, with the server wired in: connect once, for good. The popup
  // opens synchronously here so the click still counts as the gesture.
  if (server && prompt !== 'none') {
    return connectViaServer(GOOGLE_SCOPES).then((t) => { token = t; storeToken(t); resumedAt = 0; return t.value; });
  }

  return new Promise((resolve, reject) => {
    if (!tokenClient) {
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_OAUTH_CLIENT_ID,
        scope: GOOGLE_SCOPES,
        hint: userHint,
        login_hint: userHint,
        callback: () => {}, // replaced per-request below
        error_callback: () => {}, // replaced per-request below
      });
    }
    tokenClient.callback = (res) => {
      if (res && res.access_token) {
        token = {
          value: res.access_token,
          // 60s safety margin before the reported expiry.
          expiresAt: Date.now() + (Number(res.expires_in || 3600) - 60) * 1000,
        };
        storeToken(token);
        try { localStorage.setItem(GRANTED_KEY, '1'); } catch (e) { /* private mode */ }
        resolve(token.value);
      } else {
        reject(new Error((res && res.error) || 'No access token returned'));
      }
    };
    tokenClient.error_callback = (err) => {
      reject(new Error((err && (err.message || err.type)) || 'Google sign-in cancelled'));
    };
    // prompt:'' → no forced re-consent; GIS still shows the chooser/consent
    // the first time, then auto-closes on subsequent grants.
    tokenClient.requestAccessToken({ prompt });
  });
}

// Forget the cached token WITHOUT revoking the Google grant — used on sign-out
// so a shared machine can't reuse the previous user's token, while the same
// user still gets a silent re-grant next time (no re-consent).
export function clearGoogleToken() {
  token = null; storeToken(null); forgetScopes(); clearThreadCache();
  // The next person at this browser must not inherit this one's mailbox: the
  // server grant goes too. Bounded so sign-out never waits on it.
  if (!server) return Promise.resolve();
  return Promise.race([server('googleDisconnect', {}).catch(() => {}), new Promise((r) => setTimeout(r, 2500))]);
}

export function disconnectGoogle() {
  if (token && typeof google !== 'undefined' && google.accounts && google.accounts.oauth2) {
    try { google.accounts.oauth2.revoke(token.value, () => {}); } catch (e) { /* best effort */ }
  }
  token = null;
  storeToken(null);
}

// API calls never open the Google popup themselves: they run after the screen
// has painted, outside any click, where a popup either gets blocked or sits
// unanswered and the call never returns. A lapsed token is renewed silently
// (bounded); failing that, the call fails and the screen offers Connect.
async function liveToken() {
  if (!hasToken()) await resumeToken();
  if (!hasToken()) throw new Error('Google session expired — reconnect');
  return token.value;
}

async function gFetch(url) {
  const t = await liveToken();
  let res = await pacedFetch(url, { headers: { Authorization: `Bearer ${t}` } });
  if (res.status === 401 && server) {
    // Revoked or aged early — one renewal through the server, then one retry.
    token = null; storeToken(null); resumedAt = 0;
    if (await resumeToken()) res = await pacedFetch(url, { headers: { Authorization: `Bearer ${token.value}` } });
  }
  if (res.status === 401) { token = null; storeToken(null); throw new Error('Google session expired — reconnect'); }
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error.message; } catch (e) { /* keep status text */ }
    throw new Error(detail || `Google API error ${res.status}`);
  }
  return res.json();
}

const GMAIL = 'https://gmail.googleapis.com/gmail/v1/users/me';
const DRIVE = 'https://www.googleapis.com/drive/v3/files';

function header(msg, name) {
  const h = (msg.payload && msg.payload.headers || []).find(
    (x) => x.name.toLowerCase() === name.toLowerCase());
  return h ? h.value : '';
}

/** Parse "Display Name <a@b>" → {name, email}. */
function parseFrom(v) {
  const m = String(v || '').match(/^\s*"?([^"<]*)"?\s*(?:<([^>]+)>)?\s*$/);
  const email = (m && m[2]) || (String(v).includes('@') ? String(v).trim() : '');
  const name = ((m && m[1]) || '').trim() || email.split('@')[0] || '—';
  return { name, email };
}

/** One thread's summary — what a Mail row shows. Shared by listThreads and getThreadSummaries. */
// THREAD SUMMARIES, kept on this device and keyed by Gmail's historyId
// (07/10/2026). Gmail gives each person 6,000 quota units a minute on Track's
// project and a full thread costs ~30 — one Mail open of 50 threads spent a
// quarter of it, and a reload or two exhausted it (signature and Send then
// failed). threads.list already returns each thread's historyId for almost
// nothing; a thread is fetched again only when that number has moved.
const TCACHE_KEY = 'track-thread-cache';
let tcache = null;
let tcTimer = null;
function tc() {
  if (!tcache) { try { tcache = JSON.parse(localStorage.getItem(TCACHE_KEY) || '{}') || {}; } catch (e) { tcache = {}; } }
  return tcache;
}
function tcSave() {
  clearTimeout(tcTimer);
  tcTimer = setTimeout(() => {
    try {
      const keep = Object.entries(tc()).sort((a, b) => (b[1].at || 0) - (a[1].at || 0)).slice(0, 600);
      tcache = Object.fromEntries(keep);
      localStorage.setItem(TCACHE_KEY, JSON.stringify(tcache));
    } catch (e) { /* storage full or blocked: the memory copy still saves calls this visit */ }
  }, 400);
}
export function clearThreadCache() { tcache = {}; try { localStorage.removeItem(TCACHE_KEY); } catch (e) { /* ignore */ } }
async function hydrateCached(stub) {
  const hit = stub && stub.historyId ? tc()[stub.id] : null;
  if (hit && hit.h === stub.historyId && hit.v) return { ...hit.v, date: new Date(hit.v.date), snippet: stub.snippet || hit.v.snippet };
  const v = await hydrateThread(stub);
  if (v && stub && stub.historyId) { tc()[stub.id] = { h: stub.historyId, at: Date.now(), v: { ...v, date: v.date instanceof Date ? v.date.toISOString() : v.date } }; tcSave(); }
  return v;
}

async function hydrateThread(s) {
  // format=full trimmed by `fields` (Mail spec, 06/10): the same quota as
  // metadata, but it carries the part filenames (the "2 files" count and the
  // matcher's attachment signal) and To/Cc (the Mine chip). Bodies are NOT
  // requested — only attachmentId, never body/data.
  const PART = 'filename,body/attachmentId';
  const t = await gFetch(`${GMAIL}/threads/${s.id}?format=full&fields=` + encodeURIComponent(
    `id,messages(id,internalDate,labelIds,snippet,payload(headers,${PART},parts(${PART},parts(${PART},parts(${PART})))))`));
  const msgs = t.messages || [];
  const last = msgs[msgs.length - 1] || {};
  const root = msgs[0] || last; // the thread's FIRST message — its rfc822 Message-Id is
  // The row names who you are corresponding WITH, as Gmail does: the newest
  // message not sent by you. Naming the last message's sender showed your own
  // name on every thread you had replied to, so the Inbox read as sent mail
  // (Karan 06/10/2026). `lastFromMe` keeps the fact that you spoke last.
  const me = String(userHint || '').toLowerCase();
  const fromMe = (m) => !!me && String(header(m, 'From') || '').toLowerCase().includes(me);
  const theirs = [...msgs].reverse().find((m) => !fromMe(m));
  const from = parseFrom(header(theirs || last, 'From'));
  const lastFromMe = fromMe(last);
  const files = [];
  const walk = (p) => {
    if (!p) return;
    if (p.filename && p.body && p.body.attachmentId) files.push(p.filename);
    (p.parts || []).forEach(walk);
  };
  msgs.forEach((m) => walk(m.payload));
  const people = new Set();
  msgs.forEach((m) => ['From', 'To', 'Cc'].forEach((h) => {
    String(header(m, h) || '').split(',').forEach((x) => {
      const e = (x.match(/[\w.+-]+@[\w-]+\.[\w.-]+/) || [])[0];
      if (e) people.add(e.toLowerCase());
    });
  }));
  const labelIds = [...new Set(msgs.flatMap((m) => m.labelIds || []))];
  const toHead = String(header(last, 'To') || '').split(',')[0];
  return {
    labelIds,
    to: toHead ? parseFrom(toHead) : null,
    attachments: files,
    attachmentCount: files.length,
    participants: [...people],
    id: t.id,
    // Stable cross-mailbox key for manual job/category assignments (a thread has a
    // different threadId in each person's mailbox, but one Message-Id everywhere).
    messageId: header(root, 'Message-Id') || header(last, 'Message-Id') || '',
    subject: header(last, 'Subject') || '(no subject)',
    from,
    date: new Date(Number(last.internalDate || 0) || header(last, 'Date')),
    snippet: last.snippet || s.snippet || '',
    messageCount: msgs.length,
    lastFromMe,
    unread: msgs.filter((m) => (m.labelIds || []).includes('UNREAD')).length,
    starred: labelIds.includes('STARRED'),
    // Mail rules' Ignore (07/10/2026): an auto-reply says so in its headers.
    headers: { autoSubmitted: header(last, 'Auto-Submitted') || '', precedence: header(last, 'Precedence') || '' },
  };
}

// ── Drive's changes feed (live refresh, 07/10/2026) ─────────────────────────
// Read every 30 s while a Drive screen is open and visible: one cheap call that
// says what changed anywhere the person can see, My Drive and shared drives.
// Trimmed to what the screen needs: the id, its folder(s), removed, when.
const CHANGES = 'https://www.googleapis.com/drive/v3/changes';
export async function driveStartPageToken() {
  const r = await gFetch(`${CHANGES}/startPageToken?supportsAllDrives=true`);
  return r.startPageToken;
}
/** Every change since `pageToken` → { changes, next } (next = the token for the following read). */
export async function driveChanges(pageToken) {
  const changes = [];
  let tok = pageToken;
  for (let i = 0; i < 10 && tok; i++) {   // ten pages of 500 is plenty for 30 seconds
    const r = await gFetch(`${CHANGES}?pageToken=${encodeURIComponent(tok)}&pageSize=500&supportsAllDrives=true`
      + '&includeItemsFromAllDrives=true&includeRemoved=true&fields=nextPageToken,newStartPageToken,changes(fileId,removed,time,file(id,parents))');
    changes.push(...(r.changes || []));
    if (r.newStartPageToken) return { changes, next: r.newStartPageToken };
    tok = r.nextPageToken;
  }
  return { changes, next: tok };
}

/** Summaries for known thread ids (the Snoozed view: Track's own snoozes). */
export async function getThreadSummaries(ids = []) {
  const out = await mapStream(ids.map((id) => ({ id })), (st) => hydrateThread(st).catch(() => null), { limit: 10 });
  return out.filter(Boolean);
}

/**
 * List the signed-in user's threads (optionally filtered by a Gmail query) and
 * hydrate each with metadata headers. Bounded: maxResults defaults to 6.
 */
export async function listThreads({ q = '', maxResults = 6, pageToken = '', includeSpamTrash = false, labelIds = [], onFirst = null, first = 10 } = {}) {
  const list = await gFetch(`${GMAIL}/threads?maxResults=${maxResults}` +
    (q ? `&q=${encodeURIComponent(q)}` : '') +
    labelIds.map((id) => `&labelIds=${encodeURIComponent(id)}`).join('') +
    (includeSpamTrash ? '&includeSpamTrash=true' : '') +
    (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ''));
  const stubs = list.threads || [];
  // Hydrate ten at a time — 40+ parallel calls can trip Gmail's per-second
  // quota (Karan 07/07) — but as a SLIDING window, not serial chunks of ten:
  // fifty threads used to cost five round trips one after another before Mail
  // drew anything (06/10/2026). `onFirst` hands the caller the top of the list
  // as soon as it is ready, so the screen paints while the rest arrives.
  const threads = await mapStream(stubs, hydrateCached, { limit: 6, first, onFirst });
  threads.nextPageToken = list.nextPageToken || ''; // carried on the array for "Load more"
  threads.total = Number(list.resultSizeEstimate) || threads.length; // Gmail's estimate — the "All (212)" chip
  return threads;
}

/**
 * The person's OWN labels under a prefix ("C/" at ASP — org settings
 * mailLabelPrefix), with Gmail's own unread/total counts and colour, so the
 * chips say what Gmail's sidebar says (Karan 06/10/2026). One labels.get per
 * label: the list call carries no counts.
 */
let ownLabelsMemo = null;   // { prefix, at, value } — label counts are worth a minute, not a call per paint
export async function listOwnLabels(prefix = '') {
  if (ownLabelsMemo && ownLabelsMemo.prefix === prefix && Date.now() - ownLabelsMemo.at < 60000) return ownLabelsMemo.value;
  const value = await listOwnLabelsFresh(prefix);
  ownLabelsMemo = { prefix, at: Date.now(), value };
  return value;
}
async function listOwnLabelsFresh(prefix = '') {
  const r = await gFetch(`${GMAIL}/labels`);
  const mine = (r.labels || []).filter((l) => l.type === 'user' && (!prefix || String(l.name).startsWith(prefix)));
  const full = await Promise.all(mine.map((l) => gFetch(`${GMAIL}/labels/${encodeURIComponent(l.id)}`).catch(() => l)));
  const hex = (c) => (c && /^#[0-9a-f]{6}$/i.test(c) ? c : null);
  return full.map((l) => ({
    id: l.id, name: l.name, short: prefix && l.name.startsWith(prefix) ? l.name.slice(prefix.length) : l.name,
    unread: Number(l.threadsUnread) || 0, total: Number(l.threadsTotal) || 0,
    color: hex(l.color && l.color.backgroundColor),
  })).sort((a, b) => a.short.localeCompare(b.short));
}

/** Gmail's own counts for one label — INBOX gives the unread figure Gmail shows. */
export async function labelCounts(id) {
  const l = await gFetch(`${GMAIL}/labels/${encodeURIComponent(id)}`);
  return { unread: Number(l.threadsUnread) || 0, total: Number(l.threadsTotal) || 0 };
}

/** The person's Gmail labels as { id → name } — for the Mail label chips. */
export async function listLabels() {
  const r = await gFetch(`${GMAIL}/labels`);
  const out = {};
  (r.labels || []).forEach((l) => { out[l.id] = l.name; });
  return out;
}

/**
 * Archive a thread in the signed-in user's Gmail — removes the INBOX label
 * (reversible; the mail stays in All Mail). Needs the gmail.modify write scope,
 * requested on demand (one-time re-consent) via the incremental-scope pattern.
 * (Karan 06/08: "Real Gmail archive now".)
 */
export async function archiveThread(threadId) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/gmail.modify');
  return gFetchWith(t, `${GMAIL}/threads/${encodeURIComponent(threadId)}/modify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ removeLabelIds: ['INBOX'] }),
  });
}

// ── Writing to the person's mailbox (Mail full client, 07/10/2026) ──────────
// Star, read/unread, archive, bin and restore — Gmail's own state, so Gmail and
// Track agree. gmail.modify is asked for once, the first time; callers MUST call
// modifyToken() synchronously in the click (Safari blocks a consent popup
// opened after an await — see ensureScopedToken), then pass the promise in.
const MODIFY_SCOPE = 'https://www.googleapis.com/auth/gmail.modify';
export function modifyToken() { return ensureScopedToken(MODIFY_SCOPE); }

/** Add/remove labels on threads: { add: ['STARRED'], remove: ['INBOX'] }. */
export async function threadsModify(ids, { add = [], remove = [] } = {}, tokenP = null) {
  const t = await (tokenP || modifyToken());
  const one = (id) => gFetchWith(t, `${GMAIL}/threads/${encodeURIComponent(id)}/modify`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ addLabelIds: add, removeLabelIds: remove }),
  });
  for (let i = 0; i < ids.length; i += 5) await Promise.all(ids.slice(i, i + 5).map(one));
}

/** Move threads to the Bin, or back out of it (restore = untrash). */
export async function threadsTrash(ids, { restore = false } = {}, tokenP = null) {
  const t = await (tokenP || modifyToken());
  const one = (id) => gFetchWith(t, `${GMAIL}/threads/${encodeURIComponent(id)}/${restore ? 'untrash' : 'trash'}`, { method: 'POST' });
  for (let i = 0; i < ids.length; i += 5) await Promise.all(ids.slice(i, i + 5).map(one));
}

export function gmailThreadUrl(id) {
  // /u/ selector: route by account email so the right profile opens.
  return `https://mail.google.com/mail/u/${encodeURIComponent(userHint || 0)}/#all/${id}`;
}

// --- Full thread reading (for the in-app viewer) ---

// base64url → UTF-8 string.
function decodeBody(data) {
  const b64 = String(data).replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

// Walk a message payload: first html + first text body, plus attachment stubs.
function extractParts(payload, out) {
  if (!payload) return out;
  const mime = payload.mimeType || '';
  const body = payload.body || {};
  // An image the HTML shows in place (a signature logo) carries a Content-ID and
  // is referenced as src="cid:…" — it is part of the message, not an attachment
  // to file (08/10/2026: clients' signatures showed as empty boxes).
  const hdr = (n) => ((payload.headers || []).find((h) => h.name.toLowerCase() === n) || {}).value || '';
  const contentId = hdr('content-id').replace(/^<|>$/g, '');
  const inline = !!contentId && /^image\//i.test(mime) && !/attachment/i.test(hdr('content-disposition'));
  if (inline && (body.attachmentId || body.data)) {
    (out.inline = out.inline || []).push({ contentId, mimeType: mime, attachmentId: body.attachmentId || null, data: body.data || null });
  } else if (payload.filename && body.attachmentId) {
    out.attachments.push({ filename: payload.filename, mimeType: mime, size: Number(body.size) || 0, attachmentId: body.attachmentId, contentId: contentId || null });
  } else if (mime === 'text/html' && body.data && !out.html) {
    out.html = decodeBody(body.data);
  } else if (mime === 'text/plain' && body.data && !out.text) {
    out.text = decodeBody(body.data);
  }
  (payload.parts || []).forEach((p) => extractParts(p, out));
  return out;
}

/**
 * A whole thread with decoded message bodies, chronological. Each message:
 * { id, from, to, date, subject, html?, text?, attachments[] }.
 */
/**
 * The full live thread for an email reader, renewing the token silently first.
 * Throws when there is still no token, so the reader falls back to the stored
 * copy rather than asking for Google from inside an email.
 */
export async function liveThread(threadId) {
  if (!hasToken()) await resumeToken().catch(() => false);
  if (!hasToken()) throw new Error('Google is not connected');
  return getThreadFull(threadId);
}

export async function getThreadFull(threadId) {
  const t = await gFetch(`${GMAIL}/threads/${threadId}?format=full`);
  const header = (msg, name) => {
    const h = (msg.payload && msg.payload.headers || []).find((x) => x.name.toLowerCase() === name.toLowerCase());
    return h ? h.value : '';
  };
  return (t.messages || []).map((m) => {
    const parts = extractParts(m.payload, { html: null, text: null, attachments: [], inline: [] });
    parts.attachments.forEach((a) => { a.messageId = m.id; }); // needed to download the bytes
    return {
      id: m.id,
      threadId: t.id,
      // For replies: the RFC Message-Id and the chain, so Send threads properly.
      rfcId: header(m, 'Message-Id') || header(m, 'Message-ID'),
      references: header(m, 'References'),
      from: parseFrom(header(m, 'From')),
      to: header(m, 'To'),
      cc: header(m, 'Cc'),
      date: new Date(Number(m.internalDate) || header(m, 'Date')),
      subject: header(m, 'Subject'),
      ...parts,
    };
  });
}

/**
 * The person's Gmail signature — the primary send-as address's. Track's
 * signature module writes the preset INTO Gmail (functions/signature), so this
 * is both "the preset in Track" and "what is in Gmail" (Karan 07/10/2026).
 * Readable with gmail.readonly. Cached for the page; '' if there is none.
 */
let sigCache = null;
export async function getGmailSignature() {
  if (sigCache !== null) return sigCache;
  const r = await gFetch(`${GMAIL}/settings/sendAs`);
  const all = r.sendAs || [];
  const p = all.find((x) => x.isPrimary) || all.find((x) => x.isDefault) || all[0];
  sigCache = (p && p.signature) || '';
  return sigCache;
}

/**
 * Send a message from the person's own Gmail (Karan 06/10/2026). The gmail.send
 * scope is asked for once, on their first Send, through the connect-once grant.
 * `mime` is RFC 822 text from mail-send.js; `threadId` keeps a reply in its
 * thread. Uses the upload endpoint, which takes messages up to 35 MB.
 */
export async function sendMessage(mime, { threadId = '' } = {}) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/gmail.send');
  // ONE attempt, on the plain JSON endpoint (08/10/2026). Safari never got the
  // multipart upload endpoint's request to Gmail at all (Google's own request
  // log showed no SendMessage) — it only ever worked through the JSON fallback,
  // which was removed with the duplicate-send fix. JSON is now the path; only a
  // message too big for it (heavy attachments) uses the upload endpoint.
  let res;
  if (mime.length < 5 * 1024 * 1024) {
    const raw = btoa(mime).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    res = await pacedFetch(`${GMAIL}/messages/send`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(threadId ? { raw, threadId } : { raw }),
    });
  } else {
    const boundary = `trk_${Date.now().toString(36)}`;
    const meta = JSON.stringify(threadId ? { threadId } : {});
    const body = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${meta}\r\n`
      + `--${boundary}\r\nContent-Type: message/rfc822\r\n\r\n${mime}\r\n--${boundary}--`;
    res = await pacedFetch('https://gmail.googleapis.com/upload/gmail/v1/users/me/messages/send?uploadType=multipart', {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': `multipart/related; boundary=${boundary}` },
      body,
    });
  }
  if (res.status === 401) { clearScoped(); throw new Error('Google session expired — press Send again'); }
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error.message; } catch (e) { /* keep status */ }
    throw new Error(detail || `Gmail would not send it (${res.status})`);
  }
  return res.json();   // { id, threadId, labelIds }
}

/**
 * Swap src="cid:…" for the message's own inline images (data: URLs, which the
 * CSP allows), so a signature logo shows instead of an empty box. One
 * attachments.get per image (~5 Gmail units); a failure leaves that image out.
 */
export async function inlineCidImages(html, inline = [], messageId = '') {
  if (!html || !inline.length || !/cid:/i.test(html)) return html;
  const std = (b64) => String(b64 || '').replace(/-/g, '+').replace(/_/g, '/');
  const urls = {};
  await Promise.all(inline.map(async (p) => {
    try {
      let data = p.data;
      if (!data && p.attachmentId && messageId) data = (await gFetch(`${GMAIL}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(p.attachmentId)}`)).data;
      if (data) urls[p.contentId.toLowerCase()] = `data:${p.mimeType};base64,${std(data)}`;
    } catch (e) { /* that image stays out */ }
  }));
  return html.replace(/(["'(])cid:([^"')\s>]+)/gi, (m, q, id) => {
    const u = urls[decodeURIComponent(id).toLowerCase()];
    return u ? `${q}${u}` : m;
  });
}

/** Download a Gmail attachment's bytes as a Blob (uses the base read scope). */
export async function getAttachment(messageId, attachmentId, mimeType = 'application/octet-stream') {
  const r = await gFetch(`${GMAIL}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`);
  const b64 = String(r.data || '').replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

/**
 * List a Drive folder's children as the signed-in user. Shared-drive aware.
 * Read-only fields; bounded to one page of 50.
 */
/** Find a Drive folder whose name contains `needle` (enquiry folders etc.). */
export async function findDriveFolder(needle) {
  const q = encodeURIComponent(`name contains '${String(needle).replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);
  const out = await gFetch(`${DRIVE}?q=${q}&pageSize=5&fields=files(id,name,webViewLink)` +
    '&supportsAllDrives=true&includeItemsFromAllDrives=true&corpora=allDrives');
  return (out.files && out.files[0]) || null;
}

/** Folders whose name contains a project number, across every shared drive —
 * the raw candidates for drive-folders.js pickProjectFolder(). The fee backfill
 * sends the same query (functions/project-folder-pick.js projectFolderQuery). */
export async function searchProjectFolders(number) {
  const n = String(number || '').replace(/[^A-Za-z0-9]/g, '');
  if (!n) return [];
  const params = new URLSearchParams({
    q: `name contains '${n}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
    pageSize: '20', fields: 'files(id,name,mimeType,modifiedTime,webViewLink)',
    supportsAllDrives: 'true', includeItemsFromAllDrives: 'true', corpora: 'allDrives',
  });
  const out = await gFetch(`${DRIVE}?${params}`);
  return out.files || [];
}

/** Overwrite a Drive file's CONTENT as a new head revision (Drive keeps the
 * previous revisions — nothing is silently lost). Used by PDF markup save. */
export async function updateDriveFile(fileId, bytes, mimeType) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/drive');
  const res = await fetch(
    `https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(fileId)}?uploadType=media&supportsAllDrives=true`,
    { method: 'PATCH', headers: { Authorization: `Bearer ${t}`, 'Content-Type': mimeType }, body: bytes });
  if (res.status === 401) { clearScoped(); throw new Error('Google session expired — save again'); }
  if (!res.ok) throw new Error(`Drive save failed (${res.status})`);
  invalidateDriveDownload(fileId);   // we just wrote a new revision — never serve the old bytes
  return res.json();
}

/** Raw file bytes (ArrayBuffer) — used by the in-app DWG viewer. */
// Session LRU for Drive downloads (perf 08/08): re-opening the same sheet —
// after a close, a Compare, a tab switch — used to re-download the whole file
// every time, and the download IS the wait on big drawings. Capped at ~120MB;
// memory only (nothing written to disk); invalidated when Track itself writes
// a new revision (updateDriveFile). A colleague's newer revision arrives on
// the next session or after eviction — same freshness class as the registers.
const dlCache = new Map();                     // fileId → { bytes: ArrayBuffer, size }
let dlCacheBytes = 0;
const DL_CACHE_MAX = 120 * 1024 * 1024;
function dlRemember(fileId, bytes) {
  if (bytes.byteLength > DL_CACHE_MAX / 2) return;      // don't let one giant evict everything
  dlCacheBytes += bytes.byteLength;
  dlCache.set(fileId, { bytes, size: bytes.byteLength });
  while (dlCacheBytes > DL_CACHE_MAX && dlCache.size) {
    const [oldest, entry] = dlCache.entries().next().value;
    dlCache.delete(oldest); dlCacheBytes -= entry.size;
  }
}
export function invalidateDriveDownload(fileId) {
  const hit = dlCache.get(fileId);
  if (hit) { dlCache.delete(fileId); dlCacheBytes -= hit.size; }
}
export async function downloadDriveFile(fileId) {
  const hit = dlCache.get(fileId);
  if (hit) {
    dlCache.delete(fileId); dlCache.set(fileId, hit);   // LRU bump
    return hit.bytes.slice(0);                          // callers may transfer/detach
  }
  const t = await liveToken();
  const res = await fetch(`${DRIVE}/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${t}` } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const bytes = await res.arrayBuffer();
  dlRemember(fileId, bytes.slice(0));
  return bytes;
}

// Every child, not the first 50: it used to stop at one page and say nothing,
// so a busy Drawings folder simply lost its later files. Pages until Drive says
// there are no more (bounded at 5,000 so a pathological folder cannot hang).
// Shortcuts come back pointing at their target, so a folder shortcut (the ADIPS
// directory links) opens as a folder and a file shortcut opens the file.
export async function listFolder(folderId) {
  const out = [];
  let pageToken = '';
  do {
    const params = new URLSearchParams({
      q: `'${String(folderId).replace(/'/g, "\\'")}' in parents and trashed=false`,
      orderBy: 'folder,name',
      pageSize: '1000',
      fields: 'nextPageToken,files(id,name,mimeType,modifiedTime,webViewLink,size,lastModifyingUser(displayName),shortcutDetails(targetId,targetMimeType))',   // v4: the BY column (p15)
      supportsAllDrives: 'true',
      includeItemsFromAllDrives: 'true',
    });
    if (pageToken) params.set('pageToken', pageToken);
    const res = await gFetch(`${DRIVE}?${params}`);
    out.push(...(res.files || []).map(resolveShortcut));
    pageToken = res.nextPageToken || '';
  } while (pageToken && out.length < 5000);
  return out;
}

/**
 * The children of MANY folders in one Drive call (Karan 06/10/2026: Files was
 * slow). Drive's q takes `'a' in parents or 'b' in parents …`, so a whole level
 * of a project tree is one round trip instead of one per folder. Returns
 * Map parentId → children (shortcuts resolved). A chunk Drive says it could not
 * search completely, or that fails, is re-read one folder at a time; a folder
 * that still fails is left OUT of the map so the caller reports it unread.
 */
export async function listChildren(parentIds, { chunk = 40 } = {}) {
  const ids = [...new Set((parentIds || []).filter(Boolean).map(String))];
  const out = new Map();
  const esc = (id) => id.replace(/'/g, "\\'");
  const chunks = [];
  for (let i = 0; i < ids.length; i += chunk) chunks.push(ids.slice(i, i + chunk));
  await Promise.all(chunks.map(async (part) => {
    const got = new Map(part.map((id) => [id, []]));
    try {
      let pageToken = '';
      let complete = true;
      do {
        const params = new URLSearchParams({
          q: `(${part.map((id) => `'${esc(id)}' in parents`).join(' or ')}) and trashed=false`,
          pageSize: '1000',
          fields: 'nextPageToken,incompleteSearch,files(id,name,mimeType,modifiedTime,webViewLink,size,parents,shortcutDetails(targetId,targetMimeType))',
          supportsAllDrives: 'true', includeItemsFromAllDrives: 'true', corpora: 'allDrives',
        });
        if (pageToken) params.set('pageToken', pageToken);
        const res = await gFetch(`${DRIVE}?${params}`);
        if (res.incompleteSearch) complete = false;
        for (const f of res.files || []) {
          const r = resolveShortcut(f);
          for (const p of f.parents || []) if (got.has(p)) got.get(p).push(r);
        }
        pageToken = res.nextPageToken || '';
      } while (pageToken);
      if (!complete) throw new Error('incomplete');
      got.forEach((v, k) => out.set(k, v));
    } catch (_) {
      await Promise.all(part.map((id) => listFolder(id).then((v) => { out.set(id, v); }, () => { /* left unread */ })));
    }
  }));
  return out;
}

export function isFolderMime(mime) { return mime === 'application/vnd.google-apps.folder'; }

/** The shared drives THIS person's Google account can open (id, name, and
 * what Google lets them do there) — access follows Google, not Track roles. */
export async function listSharedDrives() {
  const out = await gFetch('https://www.googleapis.com/drive/v3/drives?pageSize=100&fields=drives(id,name,capabilities(canEdit,canComment,canAddChildren))');
  return (out.drives || []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** One file as Drive has it NOW — Files' "renamed?" re-check reads the live name
 * rather than trusting a tick. Read scope. */
export async function getDriveFile(fileId) {
  const params = new URLSearchParams({ fields: 'id,name,mimeType,modifiedTime,webViewLink,trashed', supportsAllDrives: 'true' });
  return gFetch(`${DRIVE}/${encodeURIComponent(fileId)}?${params}`);
}

/** Full-text + name search for FILES across every shared drive (5a). Returns
 * rows with driveId/parents so the caller can show "where". Read scope. */
export async function searchDriveFiles(queryStr, { max = 60 } = {}) {
  const safe = String(queryStr || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  if (!safe.trim()) return [];
  const q = `(name contains '${safe}' or fullText contains '${safe}') and trashed=false and mimeType != 'application/vnd.google-apps.folder'`;
  const params = new URLSearchParams({
    q,
    pageSize: String(Math.min(max, 100)),
    fields: 'files(id,name,mimeType,modifiedTime,webViewLink,size,parents,driveId)',
    orderBy: 'modifiedTime desc',
    supportsAllDrives: 'true', includeItemsFromAllDrives: 'true', corpora: 'allDrives',
  });
  const out = await gFetch(`${DRIVE}?${params}`);
  return out.files || [];
}

const CALENDAR = 'https://www.googleapis.com/calendar/v3';

/**
 * Calendar events between two dates (read-only, single events expanded from
 * recurrences, bounded to 250). calendarId defaults to the user's own calendar;
 * pass a shared calendar's id (e.g. the Sigma team calendar) for team views.
 */
export async function listCalendarEvents(timeMin, timeMax, calendarId = 'primary') {
  const params = new URLSearchParams({
    timeMin: timeMin.toISOString(),
    timeMax: timeMax.toISOString(),
    singleEvents: 'true',
    orderBy: 'startTime',
    maxResults: '250',
    fields: 'items(id,summary,description,status,recurringEventId,extendedProperties,start,end,attendees(email,responseStatus,self),organizer(email,displayName),creator(email,displayName),htmlLink,eventType,location,hangoutLink,conferenceData(entryPoints(uri,entryPointType)))',   // location + meeting link: the phone's Next card (v4.14.0); description + extendedProperties + recurringEventId: whether you may edit it (v4.21.6)
  });
  const res = await gFetch(`${CALENDAR}/calendars/${encodeURIComponent(calendarId)}/events?${params}`);
  return res.items || [];
}

/** The user's calendar list (own + shared calendars they subscribe to). */
export async function listCalendars() {
  const res = await gFetch(`${CALENDAR}/users/me/calendarList?fields=items(id,summary,primary,accessRole)`);
  return res.items || [];
}

// ── Targeted WRITE scopes (Karan, 06/07) ─────────────────────────────────────
// The base connection stays read-only. Specific actions (filing a receipt,
// adding a calendar event) request their write scope AT THE MOMENT OF USE via
// a separate GIS token, so users grant exactly what the action needs.
// Kept for the token's hour, like the base token. They lived in memory only, so
// every reload asked for Drive write access again the first time anyone saved
// markup or filed a receipt: a Google popup on most visits (Karan 05/10).
const SCOPED_KEY = 'track-gtoken-scoped';
let scopedTokens = null;   // scope → {value, expiresAt}; read lazily from storage
function scoped() {
  if (!scopedTokens) {
    try {
      const all = JSON.parse(localStorage.getItem(SCOPED_KEY) || '{}');
      scopedTokens = Object.fromEntries(Object.entries(all).filter(([, t]) => t && t.value && t.expiresAt > Date.now()));
    } catch (e) { scopedTokens = {}; }
  }
  return scopedTokens;
}
function clearScoped() {
  scopedTokens = {};
  try { localStorage.removeItem(SCOPED_KEY); } catch (e) { /* ignore */ }
}
const scopedPending = {};
export function ensureScopedToken(scope) {
  const cached = scoped()[scope];
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.value);
  if (!isConfigured()) return Promise.reject(new Error('Google connection not configured'));
  // One popup per scope, not one per caller: the second caller used to open a
  // second token client whose answer the first never heard.
  if (scopedPending[scope]) return scopedPending[scope];
  const keep = (t) => {
    scoped()[scope] = t;
    try { localStorage.setItem(SCOPED_KEY, JSON.stringify(scopedTokens)); } catch (e) { /* memory still works */ }
    return t.value;
  };
  if (server) {
    // Not held (as far as this device knows) → Google's consent NOW, inside the
    // click. Held → the server renews it with no popup at all.
    const ask = grantScopes().has(scope) ? serverToken(scope).then((t) => t || connectViaServer(scope)) : connectViaServer(scope);
    scopedPending[scope] = ask
      .then(keep)
      .finally(() => { delete scopedPending[scope]; });
    return scopedPending[scope];
  }
  scopedPending[scope] = new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope,
      hint: userHint,
      callback: (res) => {
        if (res && res.access_token) {
          scoped()[scope] = {
            value: res.access_token,
            expiresAt: Date.now() + (Number(res.expires_in || 3600) - 60) * 1000,
          };
          try { localStorage.setItem(SCOPED_KEY, JSON.stringify(scopedTokens)); } catch (e) { /* memory still works */ }
          resolve(res.access_token);
        } else reject(new Error((res && res.error) || 'No access token returned'));
      },
      error_callback: (err) => reject(new Error((err && (err.message || err.type)) || 'Google authorisation cancelled')),
    });
    client.requestAccessToken({ prompt: '' });
  }).finally(() => { delete scopedPending[scope]; });
  return scopedPending[scope];
}

async function gFetchWith(token, url, init = {}) {
  const res = await pacedFetch(url, { ...init, headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) } });
  // A revoked or aged-out write token must not be served from storage again.
  if (res.status === 401) { clearScoped(); throw new Error('Google session expired — try again'); }
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error.message; } catch (e) { /* keep status */ }
    throw new Error(detail || `Google API error ${res.status}`);
  }
  return res.json();
}

// resolveFinanceFolder lived here (unused since fileReceipt moved to the server,
// and it filed as deep as it got). The one finance-folder walk is public/finance-folder.js.

/** Upload a file into Drive (write scope requested on demand). */
/** The parent folder id(s) of a Drive file — for saving siblings next to it. */
export async function driveFileParents(fileId) {
  const out = await gFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=parents&supportsAllDrives=true`);
  return out.parents || [];
}

/**
 * The private Track stamps on a file. A saved _MARKUP sheet names the drawing it
 * was marked up from, so the reviewer can read one conversation off either file.
 * appProperties are invisible in Drive's UI and readable only by this app.
 */
export async function driveAppProperties(fileId) {
  const out = await gFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=appProperties&supportsAllDrives=true`);
  return out.appProperties || {};
}

export async function uploadToDrive({ name, blob, parentId, appProperties }) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/drive');
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify({ name, parents: parentId ? [parentId] : undefined, appProperties })],
    { type: 'application/json' }));
  form.append('file', blob);
  return gFetchWith(t, 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true&fields=id,name,webViewLink',
    { method: 'POST', body: form });
}

/**
 * A file in a folder carrying one private stamp, or null. A Save stamps its PDF so
 * a retry can tell "the upload got through and only the reply was lost" from "it
 * never arrived", rather than making a second PDF.
 */
export async function findDriveFileByAppProperty(parentId, key, value) {
  const esc = (v) => String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  const parent = parentId ? `'${esc(parentId)}' in parents and ` : '';
  const params = new URLSearchParams({
    q: `${parent}appProperties has { key='${esc(key)}' and value='${esc(value)}' } and trashed=false`,
    pageSize: '1', fields: 'files(id,name)', supportsAllDrives: 'true', includeItemsFromAllDrives: 'true',
  });
  const out = await gFetch(`${DRIVE}?${params}`);
  return out.files?.[0] || null;
}

/** Exact child-folder lookup, scoped to one parent (Shared Drive aware). */
export async function findDriveChild(parentId, name) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/drive');
  const q = `'${String(parentId).replace(/'/g, "\\'")}' in parents and name='${String(name).replace(/'/g, "\\'")}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  const params = new URLSearchParams({ q, pageSize: '1', fields: 'files(id,name,webViewLink)', supportsAllDrives: 'true', includeItemsFromAllDrives: 'true' });
  const out = await gFetchWith(t, `${DRIVE}?${params}`);
  return out.files?.[0] || null;
}

export async function createDriveFolder({ name, parentId }) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/drive');
  return gFetchWith(t, `${DRIVE}?supportsAllDrives=true&fields=id,name,webViewLink`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, mimeType: 'application/vnd.google-apps.folder', parents: [parentId] }),
  });
}

/** The Drive write token, asked for INSIDE a click (Safari blocks a consent
 * popup opened later). Start it at the click; await it after other work. */
export function driveWriteToken() { return ensureScopedToken('https://www.googleapis.com/auth/drive'); }

/** Rename a Drive file or folder — files.update with the NAME only (nothing
 * else about it changes; its id, parents and contents stay). Shared-drive aware.
 * tokenP: a driveWriteToken() started earlier, in the click. */
export async function renameDriveFile(fileId, name, tokenP = null) {
  const t = await (tokenP || driveWriteToken());
  return gFetchWith(t, `${DRIVE}/${encodeURIComponent(fileId)}?supportsAllDrives=true&fields=id,name`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

/** Move a Drive file or folder to Drive's BIN — files.update trashed:true, with
 * the PERSON's token, exactly as Rename writes. Drive keeps it for 30 days and it
 * can be restored (unbinDriveFile, or Drive's own bin). Track NEVER calls
 * files.delete or emptyTrash: there is no permanent delete (files-bin.js law 1).
 * A refusal throws Drive's own message (gFetchWith), e.g. a Contributor on a
 * shared drive: "The user does not have sufficient permissions for this file." */
export async function binDriveFile(fileId, tokenP = null) {
  const t = await (tokenP || driveWriteToken());
  return gFetchWith(t, `${DRIVE}/${encodeURIComponent(fileId)}?supportsAllDrives=true&fields=id,name,trashed`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trashed: true }),
  });
}
/** Undo of binDriveFile: trashed:false puts it back where it was. */
export async function unbinDriveFile(fileId, tokenP = null) {
  const t = await (tokenP || driveWriteToken());
  return gFetchWith(t, `${DRIVE}/${encodeURIComponent(fileId)}?supportsAllDrives=true&fields=id,name,trashed`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trashed: false }),
  });
}

/** Idempotency guard: a retry finds the file created by an earlier upload. */
export async function findDriveFileByCaptureId(parentId, captureId) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/drive');
  const q = `'${String(parentId).replace(/'/g, "\\'")}' in parents and appProperties has { key='trackCaptureId' and value='${String(captureId).replace(/'/g, "\\'")}' } and trashed=false`;
  const params = new URLSearchParams({ q, pageSize: '1', fields: 'files(id,name,webViewLink)', supportsAllDrives: 'true', includeItemsFromAllDrives: 'true' });
  const out = await gFetchWith(t, `${DRIVE}?${params}`);
  return out.files?.[0] || null;
}

/** Create a calendar event (events write scope requested on demand). */
export async function insertCalendarEvent(calendarId, event) {
  const t = await ensureScopedToken('https://www.googleapis.com/auth/calendar.events');
  return gFetchWith(t, `${CALENDAR}/calendars/${encodeURIComponent(calendarId)}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
  });
}

/**
 * Renew the access token WITHOUT asking, for someone who has connected before.
 *
 * A Google access token lasts an hour. Track stored it correctly and then, on
 * expiry, showed the "Connect Google Calendar" button again — so anyone with
 * Track open all day reconnected Mail and Calendar every hour, which reads as
 * "every update" because a deploy is when you reload and notice. The grant is
 * still there; only the token has aged out, and GIS will re-issue one with no
 * prompt at all for an account that has already consented.
 *
 * Resolves TRUE if a live token is now held. Never throws, and never prompts a
 * first-time user: if the renewal needs a popup it will be blocked outside a
 * gesture, and the Connect button is still there to click.
 */
//
// BOUNDED (05/10). GIS gives no callback at all when its popup is left open or
// closed under a Cross-Origin-Opener-Policy it cannot see through, so an
// unanswered renewal used to hang this promise for the life of the page — and
// Mail, Files and Calendar all await it before painting, which left them blank
// or on "Loading…" for good. It now gives up after RESUME_TIMEOUT_MS, asks with
// prompt:'none' so Google answers at once when it needs the person, and lets a
// later screen try again once the last attempt has settled.
export const RESUME_TIMEOUT_MS = 8000;
const RESUME_RETRY_MS = 60000;
let resumed = null;
let resumedAt = 0;
export function resumeToken() {
  if (hasToken()) return Promise.resolve(true);
  if (resumed) return resumed;                    // one attempt in flight
  if (Date.now() - resumedAt < RESUME_RETRY_MS) return Promise.resolve(false);
  if (server) {
    // No popup on this path at all: the server renews from the stored grant.
    resumedAt = Date.now();
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), RESUME_TIMEOUT_MS); });
    resumed = Promise.race([serverToken(), timeout]).then((t) => {
      clearTimeout(timer); resumed = null;
      if (!t) return false;
      token = t; storeToken(t); resumedAt = 0;
      return true;
    });
    return resumed;
  }
  let grantedBefore = false;
  try { grantedBefore = localStorage.getItem(GRANTED_KEY) === '1'; } catch (e) { /* ignore */ }
  if (!grantedBefore || !isConfigured()) return Promise.resolve(false);
  resumedAt = Date.now();
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(false), RESUME_TIMEOUT_MS); });
  resumed = Promise.race([ensureToken('none').then(() => true).catch(() => false), timeout])
    .then((ok) => { clearTimeout(timer); resumed = null; return ok && hasToken(); });
  return resumed;
}

/**
 * Where a folder sits on Google Drive for desktop (Files, "Open in AutoCAD",
 * turn 8): ['Shared drives', 'Projects', '8614 Ben Harrison'] or
 * ['My Drive', …]. Walks the parents up to the drive — a project folder is one
 * or two hops below it — and names the drive. Kept per folder for the session.
 * Read scope.
 */
const placeCache = new Map();
export function driveFolderPlace(folderId) {
  if (!folderId) return Promise.resolve(null);
  if (!placeCache.has(folderId)) {
    const p = (async () => {
      const names = [];
      let id = folderId;
      for (let hop = 0; hop < 12; hop++) {
        const f = await gFetch(`${DRIVE}/${encodeURIComponent(id)}?fields=id,name,parents,driveId&supportsAllDrives=true`);
        if (f.driveId && f.id === f.driveId) break;            // the shared drive's own root
        const parent = (f.parents || [])[0];
        if (!parent) {                                         // the top of My Drive
          return hop === 0 && !f.driveId ? ['My Drive'] : ['My Drive', ...names.reverse()];
        }
        names.push(f.name);
        if (f.driveId && parent === f.driveId) {
          const d = await gFetch(`https://www.googleapis.com/drive/v3/drives/${encodeURIComponent(f.driveId)}?fields=name`);
          return ['Shared drives', d.name, ...names.reverse()];
        }
        id = parent;
      }
      return null;
    })().catch((e) => { placeCache.delete(folderId); throw e; });
    placeCache.set(folderId, p);
  }
  return placeCache.get(folderId);
}

/** Drive's own thumbnail of a file, when it has one (the preview pane). Read scope. */
export async function driveThumbnail(fileId) {
  const f = await gFetch(`${DRIVE}/${encodeURIComponent(fileId)}?fields=hasThumbnail,thumbnailLink&supportsAllDrives=true`);
  return f.hasThumbnail && f.thumbnailLink ? f.thumbnailLink : null;
}
